import { Box, Typography, useTheme, Paper, styled, Button, TableContainer, TableCell, TableRow, Table, TableHead, TableBody, CircularProgress } from "@mui/material";
import jwtAxios from "../../utils/jwtAxios";
import { useState, useEffect } from "react";
import SubscriptionTable, { PricingTier } from "../subscriptions/SubscriptionTable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { AppColor } from "../../types/Constants";
import { useAuthContext } from "../authentication/contexts/AuthContext";

function SubscriptionsIndex() {
    
    let theme = useTheme();
    let auth = useAuthContext();
    let userSubscriptionData = auth.userSubscriptionData;
    const [currentSubscription, setCurrentSubscription] = useState<string | null>(null);
    
    const [subscriptionName, setSubscriptionName] = useState("Test Drive");
    const [currentInterval, setCurrentInterval] = useState<string>("");
    const [activeSubscription, setActiveSubscription] = useState("");
    const [subscriptionDate, setSubscriptionDate] = useState("");
    const [subscriptionAmount, setSubscriptionAmount] = useState("");
    const [subscriptionCancel, setSubscriptionCancel] = useState(false);
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const Item = styled(Paper)(({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
        ...theme.typography.body1,
        padding: theme.spacing(3),
        textAlign: 'center',
      }));

      useEffect(() => {
        const fetchCurrentSubscription = async () => {
          try {
            setIsLoading(true);
      
            setSubscriptionAmount(userSubscriptionData?.amount ?? "");
            setSubscriptionCancel(userSubscriptionData?.cancel_at_period_end ?? false);
            setActiveSubscription(userSubscriptionData?.status ?? "");
            const date = new Date(userSubscriptionData?.renewal_date ?? "");
            setSubscriptionDate(date.toLocaleDateString());
            setCurrentSubscription(userSubscriptionData?.current_tier ?? null);
            setCurrentInterval(userSubscriptionData?.interval ?? "");
            setSubscriptionName(userSubscriptionData?.name ?? "Test Drive");
            setCustomerId(userSubscriptionData?.stripe_id ?? null);
      
            const response = await jwtAxios.get("/api/subscription/pricingtiers");
            if (response) {
              setPricingTiers(response.data.pricing_tiers);
            }
          } catch (err) {
            console.error("Failed to fetch subscription data:", err);
          } finally {
            setIsLoading(false);
          }
        };
      
        fetchCurrentSubscription();
      }, []);
      

    const manageSubscription = (action: "renew" | "cancel" | "manage") => {
        let apiUrl = "";
            switch (action) {
                case "renew":
                    apiUrl = `/api/subscription/${action}`;
                    break;
                case "cancel":
                    apiUrl = `/api/subscription/${action}`;
                    break;
                case "manage":
                    apiUrl = `/api/subscription/${action}`;
                    break;
            }
        jwtAxios
            .post(apiUrl)
            .then(response => {
                window.location.href = response.data.url;
            })
            .catch(error => console.error("API request failed:", error));
    };

    const renderHeader = () => {
        return (
        <Box justifyContent="center">
            <Box padding={theme.spacing(8)}>
                <Typography variant="h4" align="center" mb={4} fontWeight="600">Subscription</Typography>
                <Typography variant="subtitle1" textAlign="center" maxWidth="50%" margin="auto"> Here is your current subscription to Skidattl. You can choose to upgrade your current subscription to gain access to more features and benefits.</Typography>
            </Box>
        </Box>
        );
    };

    const renderTable = () => {
        return (
            <TableContainer component={Item} sx={{ margin: "auto", marginBottom: "20px", width: "95%"}}>
                <Table size="small">
                    <TableHead>
                    <TableRow>
                        <TableCell align="left">Current Subscription</TableCell>
                        <TableCell align="left">Status</TableCell>
                        {subscriptionCancel ? ( 
                            <TableCell align="left">Subscription End Date</TableCell>
                        ) : (<TableCell align="left">Next Payment</TableCell> 
                        )}
                        <TableCell align="left">Amount</TableCell>
                        {customerId && (
                            <TableCell align="left"></TableCell>
                        )}
                    </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                        <TableCell align="left" width="30%">{subscriptionName}</TableCell>
                        {subscriptionCancel ? (
                                <TableCell align="left">Pending Cancellation</TableCell>
                            ) : (
                                <TableCell align="left"><Box sx={activeSubscription == "Active" || currentSubscription == null ? {backgroundColor: "#68A200" + 44, color: "#68A200", display: "flex", borderRadius: "5px", justifyContent: "center", width: "75%"} : {}}>{currentSubscription == null ? "Active" : activeSubscription}</Box></TableCell>
                            )}
                        <TableCell align="left">{currentSubscription == null ? "-" : subscriptionDate}</TableCell>
                        <TableCell align="left">${currentSubscription == null ? "0" : subscriptionAmount}</TableCell>
                        {customerId && (
                        <TableCell align="left"><Button variant="contained" onClick={() => manageSubscription("manage")}>Manage Billing</Button></TableCell>
                        )}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    const renderReseller = () => {
        return (
            <Box mt={10} mb={10} paddingX={10}>
                <Item sx={{width: "100%", textAlign: "left", paddingX: 10}}>
                    <Typography mb={4} variant="h5" fontWeight={600}>Value Added Reseller</Typography>
                    <Typography mb={4} variant="body1">If you are interested in being a reseller of Skidattl, we have special pricing worked out with multiple partners. Contact us to get your specific special pricing.</Typography>
                    <Box sx={{ display: "flex", justifyContent: "right" }}>
                        <Button variant="contained" onClick={() => { window.open("https://www.skidattl.com/contact-us", "_blank"); }}>Contact Us</Button>
                    </Box>
                </Item>
            </Box>
        );
    };

    if(isLoading){
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    
    );
    } else if (auth.isSubscribedUser()) {
    return (
    <Box height="100">
    {renderHeader()}
    {renderTable()}
    <SubscriptionTable currentSubscription={subscriptionName} currentInterval={currentInterval} pricingTiers={pricingTiers} dashboardRequired={true}/>
    {renderReseller()}

    <Box mt={10} mb={10} paddingX={10}>
        {!subscriptionCancel ? (
            <Item sx={{width: "100%", textAlign: "left", paddingX: 10}}>
                <Typography mb={4} variant="h5" fontWeight={600}>Cancel Subscription</Typography>
                <Typography mb={4} variant="body1" fontWeight={600}>We are sad to see you go.</Typography>
                <Typography mb={4} variant="body1">Your account will remain active until the next billing period, then you will permanently lose access to your Experiences, Beacons and Analytics.</Typography>
                <Box sx={{ display: "flex", justifyContent: "right" }}>
                    <Button variant="contained" onClick={() => manageSubscription("cancel")}>Cancel</Button>
                </Box>
            </Item>
        ) : (
            <Item sx={{width: "100%", textAlign: "left", paddingX: 10}}>
                <Typography mb={4} variant="h5" fontWeight={600}>Renew Subscription</Typography>
                <Typography mb={4} variant="body1">If you would like to renew your subscription.</Typography>
                <Box sx={{ display: "flex", justifyContent: "right" }}>
                    <Button  variant="contained" onClick={() => manageSubscription("renew")}>Renew</Button>
                </Box>
            </Item>
        )}
        
    </Box>
    </Box>
    );
  } else {
    return (
        <>
        <Box padding={theme.spacing(8)}>
            <Typography variant="h4" align="center" mb={4} fontWeight="600">Choose Your Subscription</Typography>
            <Typography variant="subtitle1" textAlign="center" maxWidth="50%" margin="auto"> Upgrade your subscription to access more features of Skidattl.</Typography>
        </Box>
        {renderTable()}
        <SubscriptionTable currentSubscription={subscriptionName} currentInterval={currentInterval} pricingTiers={pricingTiers} dashboardRequired={false}/>
        {renderReseller()}
        </>
    );
  }
}
  
  export default SubscriptionsIndex;